import { faPencil } from "@fortawesome/free-solid-svg-icons";
import { faTrashCan } from "@fortawesome/pro-regular-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useEffect, useState } from "react";
import { Link } from "react-router";

import "../assets/scss/components/_mini-cart-item.scss";
import Config from "../Config";
import { useCartStore } from "../stores/CartStore";
import { useProductStore } from "../stores/ProductStore";
import type { MiniCartItemProps } from "../types/components/MiniCartItemProps";
import { DateInfo } from "../utilities/DateUtils";
import { Image } from "./Image";

export const MiniCartItem = ({
	image,
	productName,
	size,
	price,
	cartKey,
	addons,
	deliveryDate,
	deliveryType,
	deliveryAddress,
	messageCard,
	removeButtonShown = true,
	messageCardType = "standard",
	personalisedCardID = "",
	personalisation = null,
	personalisedImageShown = false,
	videoMessage = false,
	editButtonShown = true,
}: MiniCartItemProps) => {
	const { RemoveFromCart } = useCartStore(({ RemoveFromCart }) => ({ RemoveFromCart }));
	const { availableAddons } = useProductStore(({ availableAddons }) => ({ availableAddons }));

	const [dateInformation, setDateInformation] = useState(DateInfo(deliveryDate, false));
	const personalisationReference = personalisation?.order_reference ?? personalisedCardID;
	const personalisationPreview =
		personalisation?.front_page_thumbnail_url ||
		(personalisationReference ? Config.getPersonalisedCardPreviewUrl(personalisationReference) : "");

	useEffect(() => {
		setDateInformation(DateInfo(deliveryDate, true));
	}, [deliveryDate]);

	return (
		<div className="basket-item row justify-content-space-between align-items-start">
			<div className="image col-2">
				<Image src={image?.replace(".local", ".co.uk")} alt={productName} width="200" />
			</div>
			<div className="details col-fluid">
				<strong className="title">{productName}</strong>
				<div className="options">
					<div className="option">{size}</div>
					{deliveryDate !== "" && (
						<div className="option">{`${dateInformation?.pretty?.short?.date} ${dateInformation?.pretty?.short?.month} Delivery`}</div>
					)}
					{deliveryType && <div className="option">{deliveryType}</div>}
				</div>

				{deliveryAddress && deliveryAddress !== "" && (
					<div className="address options">
						<div className="option">Delivering To: {deliveryAddress}</div>
					</div>
				)}

				{messageCard && messageCard !== "" && (
					<div className="message-card options">
						<div className="option">With The Gift Card: "{messageCard}"</div>
					</div>
				)}

				{personalisationReference && (
					<div className="message-card options">
						<div className="option">
							With A Personalised Gift Card{" "}
							{personalisedImageShown && personalisationPreview && (
								<img
									className="personalised-card-preview"
									src={personalisationPreview}
									alt="Personalised card preview"
								/>
							)}
						</div>
					</div>
				)}

				{videoMessage && (
					<div className="message-card options">
						<div className="option">With A Video Message</div>
					</div>
				)}

				{typeof addons !== "undefined" &&
					Object.keys(addons).length !== 0 &&
					typeof availableAddons !== "undefined" &&
					Object.keys(availableAddons).length !== 0 && (
						<div className="addons options">
							{Object.keys(addons).length === 1 ? "Addon: " : "Addons: "}
							{Object.keys(addons).map((addonIndex) => {
								const addon = addons[addonIndex];

								let addonName = "";
								if (addon.addon_name) {
									addonName = addon?.addon_name ? addon.addon_name : addonName;
								} else if (typeof addon !== "object") {
									addonName = availableAddons[addon]?.pretty_name
										? availableAddons[addon].pretty_name
										: addonName;
								} else {
									const addonId = addon?.id ? addon.id : 0;
									let foundAddon = null;

									if (availableAddons[addonId]) {
										foundAddon = availableAddons[addonId] ? availableAddons[addonId] : foundAddon;
									}

									addonName = foundAddon?.pretty_name ? foundAddon.pretty_name : addonName;
								}

								if (addonName.substring(0, 2) === "pw" || addonName.substring(0, 2) === "pc") {
									delete addons[addonIndex];
									return;
								}

								addonName = Object.keys(addons).length - addonIndex > 2 ? addonName + ", " : addonName;
								addonName =
									Object.keys(addons).length > 1 &&
									parseInt(addonIndex) === Object.keys(addons).length - 1
										? " & " + addonName
										: addonName;
								return addonName;
							})}
						</div>
					)}
			</div>
			<div className="col-3 actions">
				<span className="price">£{parseFloat(price).toFixed(2)}</span>
				{removeButtonShown && (
					<div className="remove-button">
						<FontAwesomeIcon
							icon={faTrashCan}
							onClick={() => {
								RemoveFromCart(cartKey);
							}}
						/>
					</div>
				)}

				{editButtonShown && (
					<Link className="edit-item" to={`/edit-product/${cartKey}`}>
						<FontAwesomeIcon icon={faPencil} />
					</Link>
				)}
			</div>
		</div>
	);
};
